<template>
    <div class="card">

        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="true"
                :showAddNew="false"
                title="Investigation Bill"
                @onClickCloseButton="goToList"
            />
        </div>

        <div class="my-2 px-2">
            <TableHeaderHospitalBillAdd
                :key="componentKey"
                :formData="formData"
                :tableItems="tableItems"
            />
        </div>

        <div class="col-12 px-2">
            <ListTable 
                :tableItems="tableItems"
                @addNewItem="addNewItem"
                :salesAccountHeads="salesAccountHeads"
                :vatRate="vatRate"
                ref="itemDetailsData"
            />
        </div>
        <div class="row px-2">
            <div class="col-md-6">
                <div class="mb-1">
                    <label class="form-label" for="description">Ledger Memo</label>
                    <vField
                        as="textarea"
                        name="description"
                        type="number"
                        class="form-control"
                        v-model="formData.description"
                    />
                </div>
            </div>
            <div class="col-md-5 ml-auto mt-auto mb-1">
                <div class="d-flex flex-wrap gap-1 gy-2">
                    <button :disabled="loading" @click="handleSubmit(false)" class="btn btn-primary">Save</button>
                    <button :disabled="loading" @click="handleSubmit(true)" class="btn btn-primary">Save & Print</button>
                </div>
            </div>
        </div>
        
    </div>
</template>

<script setup>
import {inject, onMounted, provide, reactive, ref} from 'vue';
import TitleButton from '@/components/atom/TitleButton';
import ListTable from '@/components/molecule/company/hospital/billing/investigation/InvoiceHospitalTable.vue';
import {useRoute, useRouter} from 'vue-router';
import {generateUUID} from '@/services/utils/global';
import { useAsyncDropdownHelper } from "@/services/utils/asyncDropdownHelper";
import handlePurchase from '@/services/modules/purchase';
import {vatRate} from '@/data/inventory.js';
import handleCompany from "@/services/modules/company";
import hospitalPdfPrinterHelper from '@/services/utils/hospitalPdfPrinterHelper';
import TableHeaderHospitalBillAdd from "@/components/molecule/company/hospital/billing/investigation/InvestigationTableHead.vue";
import useDate from "@/services/utils/day";

    const { generatePdf } = hospitalPdfPrinterHelper();
    const { fetchProductList, fetchSalesBill, ...rest } = handlePurchase();
    const { fetchCompanyInfo } = handleCompany();

    const $route = useRoute();
    const $router = useRouter();
    const date = useDate();

    const companyId = $route.params.companyId;
    const loader = ref(true);
    const salesAccountHeads = ref([]);
    const totals = ref({});
    const invoiceRes = ref({});
    const itemDetailsData = ref(null);
    const company = ref({});
    const componentKey = ref(0);
    const loading = ref(false);

    let formData = ref({
        company_id: companyId,
        contact_profile_id: null,
        referrer_id: null,
        account_head_id: null,
        ipd_register_id: null,
        vat_payable_account_head_id: null,
        service_resource_id: null,
        status: 'active',
        mop_account_head_id: null,
        contact_profile_client_id: null,
        transport_reference: null,
        receipt_reference: null,
        discount_head_id: null,
        adjustment_amount: 0,
        referrer_discount_amount: 0,
        receipt_description: "Invoice bill payment",
        payment: null,
        date: '',
        has_item_detail: true,
        description: '',
        order_number: null,
        lc_number: null,
        item_details: []
    });

    provide('formData', formData);
    provide('totals', totals);
    const showSuccess =  inject('showSuccess');
    const showError =  inject('showError');

    onMounted( async() => {
        const companyQuery = `?company_id=${companyId}`;
        const receiveAbleRes = rest.fetchAccountReceivable(companyQuery);
        const vatPayableRes = rest.fetchVatPayable(companyQuery);
        const fetchCompany = fetchCompanyInfo(companyId)
        const salesAccountHeadsRes = rest.getAccountHeadBySlag("discount_account", companyQuery)

        Promise.all([
            receiveAbleRes.then(res=> {
                if(res.data) formData.value.account_head_id = res.data[0].id;
            }),
            vatPayableRes.then(res=> {
                if(res.data) formData.value.vat_payable_account_head_id = res.data[0].id;
            }),
            fetchCompany.then(res=> {
                if(res.data) company.value = res.data;
            }),
            salesAccountHeadsRes.then(res => {
                if(res.data){
                  salesAccountHeads.value = res.data
                  formData.value.discount_head_id = res.data[0].id
                }
            })
        ])
        .then(() => {loader.value=false})
        .catch(()=>{loader.value=false})
    })

    let tableItems = reactive([]);

    const addNewItem = () => {

        tableItems.push({
            id: generateUUID(),
            product_id: null,
            name: '',
            description: '',
            quantity : 1,
            rate: 0,
            discount_percent: 0,
            vat: 15,
            sub_total: 0,
            discount_amount: 0,
            vat_amount: 0,
            total: 0,
            isEdit: false,
        });

    }

    const resetForm = () => {
        tableItems.splice(0);
        componentKey.value += componentKey.value + 1

        formData.value.company_id = companyId;
        formData.value.contact_profile_id = null;
        formData.value.contact_profile_client_id = null;
        formData.value.transport_reference = null;
        formData.value.referrer_id = null;
        formData.value.ipd_register_id = null;
        formData.value.service_resource_id = null;
        formData.value.status = 'active';
        formData.value.receipt_reference = null;
        formData.value.adjustment_amount = 0;
        formData.value.referrer_discount_amount = 0;
        formData.value.receipt_description = "Invoice bill payment";
        formData.value.payment = null;
        formData.value.date = '';
        formData.value.has_item_detail = true;
        formData.value.description = '';
        formData.value.order_number = null;
        formData.value.lc_number = null;
        formData.value.item_details = [];
        itemDetailsData.value.resetData();
    }

    const goToList = () => {

        if($route.query.contactId){
            $router.push({
                name: "patient-register",
                params: $route.params,
                query: $route.query,
            });
            return;
        }

        $router.push({name: 'investigation-bill-list',
            params: {
                companyId: $route.params.companyId,
                moduleId: $route.params.moduleId,
                menuId: $route.params.menuId,
                pageId: $route.params.pageId
            },
            query: {
                start: $route.query.start,
                end: $route.query.end
            }

        })
    }

    const handleSubmit = async (print=false) => {
        if(!formData.value.date) formData.value.date = date.currentDate();
        formData.value.item_details = tableItems;
        let copyFormData = JSON.parse(JSON.stringify(formData.value))
        copyFormData.item_details = formData.value.item_details
        copyFormData.sale_type = "investigation_invoice"
        copyFormData.receipt_type = "investigation_invoice"

        loading.value = true

        await rest.storeBillPayment(copyFormData)
        .then(res => {
            if(res.status) {
                showSuccess(res.message)
                resetForm();
                if(print) goToPrint(res.data.id)
                return;
            }
            showError(res.message)
        })
        .catch(err=> {
            showError(err.response.data.message)
        })
        .finally(() => {
            loading.value = false
        })
    }

    const goToPrint = (id) => {
        const companyQuery = `?company_id=${companyId}`;

        fetchSalesBill(id, companyQuery).then( (res) =>{
            if( res ){
                invoiceRes.value = res.data;
            }
        }).then( () => {
            generatePdf(company.value, invoiceRes.value, 'BILL', true, true, false, true)
        }).catch( (err) => {
            console.log(err);
        })
    }

</script>
<style scoped>
address{
    margin-bottom: 0 !important;
}
</style>